import { abbr, NumberVariants } from '@talos/kyoko';

/** Formats a number as a value in a chart series (y-axis tick + tooltip). */
export type SignalsValueFormatter = (value: number) => string;

/** Green for a positive value, red for negative, neutral at zero — shared across Signals modules. */
export function numberSignVariant(value: number): NumberVariants | undefined {
  return value > 0 ? NumberVariants.Positive : value < 0 ? NumberVariants.Negative : undefined;
}

/**
 * True when `value` displays as zero at `increment` — i.e. a magnitude too small for
 * `InlineFormattedNumber`'s truncation-toward-zero to show at all. A caller pairing this with
 * `numberSignVariant` should skip the sign color when this is true: a value that renders as "0.00" (or
 * "0.0000", etc.) must not still show green/red just because it's technically nonzero. `increment` must
 * be the exact same increment passed to `InlineFormattedNumber` so the two can't drift apart.
 */
export function isFlatAtIncrement(value: number, increment: string): boolean {
  return Math.abs(value) < Number(increment);
}

/**
 * CoinMetrics reports the aggregate funding rate as a dimensionless fraction (e.g. 0.004872).
 * Every funding rate surface (chart axis, legend, blotter cell) displays it as a percent, so
 * scale by 100 (0.004872 → 0.4872%). Single source of truth for the ×100 convention.
 */
export const fundingRateFractionToPercent = (value: number): number => value * 100;

/** Order-sensitive equality for two string lists — used to skip no-op persisted-selection writes. */
export function sameStringList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * True when `exchangeAsset` (e.g. `binance-btc`) is a pair for `cmAssetId` (e.g. `btc`). CoinMetrics
 * exchange-asset ids are always `<exchange>-<asset>`, so matching the `-<id>` suffix is sufficient.
 */
export function isExchangeAssetPairFor(exchangeAsset: string, cmAssetId: string): boolean {
  return exchangeAsset.endsWith(`-${cmAssetId}`);
}

/**
 * Strips the `-<cmAssetId>` suffix off an exchange-asset pair (e.g. `binance-btc`, `btc` → `binance`).
 * Returns the pair unchanged if it isn't actually a pair for that asset.
 */
export function exchangeFromPair(exchangeAsset: string, cmAssetId: string): string {
  const suffix = `-${cmAssetId}`;
  return exchangeAsset.endsWith(suffix) ? exchangeAsset.slice(0, -suffix.length) : exchangeAsset;
}

/** Formats a USD value with a thousands suffix (e.g. `$1.23B`). Shared across Signals modules. */
export const abbreviateUsd: SignalsValueFormatter = value => {
  const { number, suffix } = abbr(value, 2);
  return `$${number}${suffix}`;
};

/** Abbreviates a number with a thousands suffix and 2 decimal places but no unit sign (e.g. `1.23B`). */
export const abbreviateValue: SignalsValueFormatter = value => {
  const { number, suffix } = abbr(value, 2);
  return `${number}${suffix}`;
};

/** Compact integer count (e.g. `1.23B`) — no currency prefix, no fractional digits. */
export const abbreviateCount: SignalsValueFormatter = value => {
  const { number, suffix } = abbr(value, 0);
  return `${number}${suffix}`;
};

/** Percentage from a 0–100 numeric (e.g. `12.34%`). Two fraction digits keep small moves legible. */
export const formatPercent: SignalsValueFormatter = value =>
  `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

// Signed percentage with up to 4 fraction digits (e.g. `+0.4872%`). Funding rates move in
// tiny fractions of a percent, so legend values need more precision than the axis formatter.
export const formatSignedPercent: SignalsValueFormatter = value => {
  const sign = value >= 0 ? '+' : '-';
  const abs = Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 4 });
  return `${sign}${abs}%`;
};

// K/M suffix on large values keeps y-axis labels from overflowing the chart margin.
export const formatRelativePercent: SignalsValueFormatter = value => {
  const sign = value >= 0 ? '+' : '-';
  const { number, suffix } = abbr(Math.abs(value), 1);
  const formatted = suffix ? `${number}${suffix}` : Math.abs(value).toFixed(1);
  return `${sign}${formatted}%`;
};

// Abbreviates ≥1000 for axis compactness; preserves 4 decimal places for small values.
export const formatNumberDefault: SignalsValueFormatter = value => {
  if (Math.abs(value) >= 1000) {
    return abbreviateCount(value);
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

/**
 * Like {@link resolveSignalsValueFormatter} but omits the currency prefix for USD metrics.
 * Use for tooltip values where the metric label already provides context (e.g. `500.01M` not `$500.01M`).
 */
export function resolveSignalsTooltipFormatter(metric: string, unit?: string): SignalsValueFormatter {
  const base = resolveSignalsValueFormatter(metric, unit);
  return base === abbreviateUsd ? abbreviateValue : base;
}

// Resolves unit → metric-token inference → default, in that order.
export function resolveSignalsValueFormatter(metric: string, unit?: string): SignalsValueFormatter {
  // Unit (from CoinMetrics reference-data) is the authoritative signal when present.
  // Lowercase compare so `USD` / `usd` / `Usd` all match — CoinMetrics has been inconsistent.
  const normalizedUnit = unit?.trim().toLowerCase();
  switch (normalizedUnit) {
    case 'usd':
      return abbreviateUsd;
    case 'count':
    case 'transactions':
      return abbreviateCount;
    case '%':
    case 'percent':
    case 'percentage':
      return formatPercent;
  }

  // Token-based fallback so the chart still formats sensibly before reference-data
  // loads. Splits the metric ID on underscores AND camelCase boundaries, lowercases
  // each piece, and matches against known unit tokens. Works for both `CapMrktEstUSD`
  // and `volume_reported_spot_usd_1d` shapes.
  const tokens = extractMetricTokens(metric);
  if (tokens.has('usd')) {
    return abbreviateUsd;
  }
  if (tokens.has('pct') || tokens.has('percent') || tokens.has('percentage')) {
    return formatPercent;
  }
  if (tokens.has('cnt') || tokens.has('count')) {
    return abbreviateCount;
  }

  return formatNumberDefault;
}

// Acronym alternative must come first — prevents "U","S","D" splits on "USD" (no lookbehind; Safari < 16.4).
const CAMEL_CASE_TOKEN = /[A-Z]+(?=[A-Z][a-z\d]|$)|[A-Z][a-z\d]*|[a-z\d]+/g;

function extractMetricTokens(metric: string): Set<string> {
  const tokens = new Set<string>();
  for (const segment of metric.split('_')) {
    if (!segment) {
      continue;
    }
    for (const part of segment.match(CAMEL_CASE_TOKEN) ?? []) {
      tokens.add(part.toLowerCase());
    }
  }
  return tokens;
}
